import { Injectable } from '@angular/core';
import { pullAt, map, trim, split, forEach, get, filter } from 'lodash-es';
import { Observable } from 'rxjs';

export type Callback = (data: unknown) => string;

@Injectable({
  providedIn: 'root',
})
export class CsvService {
  private readonly toEscape = /[,"\n]/;

  readTextFile(blob: Blob): Observable<string> {
    return new Observable((obs) => {
      if (!(blob instanceof Blob)) {
        obs.error(new Error('`blob` must be an instance of File or Blob.'));
        return;
      }

      const reader = new FileReader();

      reader.onerror = (err) => obs.error(err);
      reader.onabort = (err) => obs.error(err);
      reader.onload = () => obs.next(reader.result!.toString());
      reader.onloadend = () => obs.complete();

      return reader.readAsText(blob);
    });
  }

  parseCSV(csv: string, hasHeader = true): { data: string[][]; header: string[] } {
    let data: string[][] = [];
    let header: string[] = [];

    const rows = this.parseRows(csv);

    if (hasHeader && rows.length) {
      header = this.parseCells(pullAt(rows, 0)[0]);
    }
    data = map(rows, (row) => this.parseCells(row));

    return { data, header };
  }

  // Split rows, not currently handling escaped \n
  // Filter out empty rows
  private parseRows(csv: string): string[] {
    return map(
      filter(split(csv, '\n'), (row: string) => !!row.length),
      (row) => trim(row)
    );
  }

  private parseCells(row: string): string[] {
    // Tab delimited is easy
    if (row.includes('\t')) {
      return map(split(row, '\t'), (cell) => trim(cell));
    }
    const cells: string[] = [];
    let status: 'start' | 'escaped' | 'raw' | 'after' = 'start';
    for (let index = 0; index < row.length; index++) {
      const test = row[index];
      if (status === 'start' && test === ' ') {
        // Trim spaces
        continue;
      }
      switch (status) {
      case 'start':
        if (test === '"') {
          status = 'escaped';
          cells.push('');
        } else if (test === ',') {
          cells.push('');
        } else {
          status = 'raw';
          cells.push(test);
        }
        break;
      case 'after':
        if (test === ',') {
          status = 'start';
        }
        break;
      case 'raw':
        if (test === ',') {
          status = 'start';
        } else {
          cells[cells.length - 1] += test;
        }
        break;
      case 'escaped':
        if (test === '"') {
          if (row[index + 1] === '"') {
            cells[cells.length - 1] += test;
            index++;
          } else {
            status = 'after';
          }
        } else {
          cells[cells.length - 1] += test;
        }
        break;
      }
    }
    return map(cells, (cell) => trim(cell));
  }

  create(data: Array<unknown>, columns: Array<string | Callback>, headerRow?: Array<string>): string {
    const lines: Array<string> = [];

    if (headerRow) {
      lines.push(this.createRow(headerRow));
    }

    forEach(data, (line) => {
      lines.push(
        map(columns, (column) => {
          if (typeof column === 'string') {
            return this.escapeCell(get(line, column, ''));
          } else {
            return this.escapeCell(column(line));
          }
        }).join()
      );
    });

    return lines.join('\n') + '\n';
  }

  private createRow(cells: Array<string>): string {
    return map(cells, (cell) => this.escapeCell(cell)).join(',');
  }

  private escapeCell(cell: string): string {
    if (this.toEscape.test(cell)) {
      return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
  }
}
